// What a shop owes its suppliers: a bill beside a receipt (or on its own), money paid, and
// credit for goods sent back or a corrected price. Payments and credits are set against
// bills by allocations; what is owed is summed, never stored. Money rows are voided, never
// edited. Each builder returns the ops of exactly one sync_push tx.
import type { BuildContext } from './documents'
import { CoreError } from './errors'
import type { Milli } from './qty'
import { mulDivRound } from './rounding'
import type {
  PaymentMethod,
  StockMovementRow,
  SupplierAllocationRow,
  SupplierBillRow,
  SupplierCreditLineRow,
  SupplierCreditRow,
  SupplierPaymentRow,
  SyncOp,
  SyncRowBase,
} from './rows'

declare const crypto: { randomUUID(): string }

type Paisa = number

function base(ctx: BuildContext, id?: string): SyncRowBase {
  return {
    id: id ?? (ctx.newId ?? (() => crypto.randomUUID()))(),
    tenant_id: ctx.tenantId,
    created_at: ctx.now,
    updated_at: ctx.now,
    deleted_at: null,
  }
}

function op(table: string, row: object): SyncOp {
  return { table, op: 'upsert', row: { ...row } }
}

const text = (value: string | null | undefined) => value?.trim() || null

function positive(value: number, what: string) {
  if (!Number.isSafeInteger(value) || value <= 0)
    throw new CoreError('invalid_input', `${what} must be a positive amount`)
}

const ACTIVE = { status: 'active', voided_at: null, void_reason: null } as const

// -----------------------------------------------------------------------------
// Sharing an amount out over bills
// -----------------------------------------------------------------------------

export interface OpenBill {
  id: string
  /** What is still owed on it. */
  owedPaisa: Paisa
  /** Due date, or the bill date when it has none (ISO date). */
  dueDate: string
}

/**
 * An amount set against bills, oldest due first, each up to what it owes. What is left over
 * after every bill is covered is not allocated: it stays as the supplier's advance.
 */
export function spreadPayment(
  amountPaisa: Paisa,
  bills: readonly OpenBill[],
): { billId: string; amountPaisa: Paisa }[] {
  let left = Math.max(0, amountPaisa)
  const out: { billId: string; amountPaisa: Paisa }[] = []
  const oldest = [...bills]
    .filter((b) => b.owedPaisa > 0)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.id.localeCompare(b.id))
  for (const bill of oldest) {
    if (left <= 0) break
    const share = Math.min(left, bill.owedPaisa)
    out.push({ billId: bill.id, amountPaisa: share })
    left -= share
  }
  return out
}

export interface AllocationInput {
  billId: string
  amountPaisa: Paisa
}

function allocationRows(
  ctx: BuildContext,
  source: { payment_id: string | null; credit_id: string | null },
  amount: Paisa,
  allocations: readonly AllocationInput[] | undefined,
): SupplierAllocationRow[] {
  const list = (allocations ?? []).filter((a) => a.amountPaisa !== 0)
  for (const a of list) positive(a.amountPaisa, 'an allocation')
  if (list.reduce((sum, a) => sum + a.amountPaisa, 0) > amount)
    throw new CoreError('over_allocated', 'more is set against bills than was paid or credited')
  if (new Set(list.map((a) => a.billId)).size !== list.length)
    throw new CoreError('invalid_input', 'a bill is listed twice')
  return list.map((a) => ({
    ...base(ctx),
    bill_id: a.billId,
    ...source,
    amount_paisa: a.amountPaisa,
  }))
}

// -----------------------------------------------------------------------------
// Bill
// -----------------------------------------------------------------------------

export interface BuildSupplierBillInput {
  id?: string
  supplierId: string
  /** The delivery it came with; left out for an old balance or a charge without stock. */
  goodsReceiptId?: string | null
  billNo?: string | null
  billDate: string
  dueDate?: string | null
  totalPaisa: Paisa
  notes?: string | null
}

export function buildSupplierBill(
  ctx: BuildContext,
  input: BuildSupplierBillInput,
): { bill: SupplierBillRow; ops: SyncOp[] } {
  positive(input.totalPaisa, 'the bill total')
  const bill: SupplierBillRow = {
    ...base(ctx, input.id),
    supplier_id: input.supplierId,
    goods_receipt_id: input.goodsReceiptId ?? null,
    bill_no: text(input.billNo),
    bill_date: input.billDate,
    due_date: input.dueDate || null,
    total_paisa: input.totalPaisa,
    notes: text(input.notes),
    ...ACTIVE,
  }
  return { bill, ops: [op('supplier_bills', bill)] }
}

/** A bill's number, due date and notes may change; nothing else. */
export function editSupplierBill(
  ctx: BuildContext,
  bill: SupplierBillRow,
  changes: { billNo?: string | null; dueDate?: string | null; notes?: string | null },
): { bill: SupplierBillRow; ops: SyncOp[] } {
  if (bill.status !== 'active') throw new CoreError('immutable', 'a cancelled bill cannot change')
  const next: SupplierBillRow = {
    ...bill,
    bill_no: changes.billNo === undefined ? bill.bill_no : text(changes.billNo),
    due_date: changes.dueDate === undefined ? bill.due_date : changes.dueDate || null,
    notes: changes.notes === undefined ? bill.notes : text(changes.notes),
    updated_at: ctx.now,
  }
  return { bill: next, ops: [op('supplier_bills', next)] }
}

// -----------------------------------------------------------------------------
// Payment
// -----------------------------------------------------------------------------

export interface BuildSupplierPaymentInput {
  id?: string
  supplierId: string
  amountPaisa: Paisa
  method: PaymentMethod
  paymentDate: string
  reference?: string | null
  notes?: string | null
  /** Bills it pays. Whatever is not allocated stays as an advance with the supplier. */
  allocations?: readonly AllocationInput[]
}

export function buildSupplierPayment(
  ctx: BuildContext,
  input: BuildSupplierPaymentInput,
): { payment: SupplierPaymentRow; allocations: SupplierAllocationRow[]; ops: SyncOp[] } {
  positive(input.amountPaisa, 'the payment')
  const payment: SupplierPaymentRow = {
    ...base(ctx, input.id),
    supplier_id: input.supplierId,
    payment_date: input.paymentDate,
    method: input.method,
    amount_paisa: input.amountPaisa,
    reference: text(input.reference),
    notes: text(input.notes),
    ...ACTIVE,
  }
  const allocations = allocationRows(
    ctx,
    { payment_id: payment.id, credit_id: null },
    input.amountPaisa,
    input.allocations,
  )
  return {
    payment,
    allocations,
    ops: [
      op('supplier_payments', payment),
      ...allocations.map((r) => op('supplier_allocations', r)),
    ],
  }
}

// -----------------------------------------------------------------------------
// Credit: goods sent back, or a corrected price
// -----------------------------------------------------------------------------

export interface SupplierCreditLineInput {
  productId: string
  qtyMilli: Milli
  unitCostPaisa: Paisa
  batchId?: string | null
}

export interface BuildSupplierCreditInput {
  id?: string
  supplierId: string
  goodsReceiptId?: string | null
  creditNo?: string | null
  creditDate: string
  reason?: string | null
  notes?: string | null
  /** Goods that go back: each leaves stock at `locationId`. */
  lines?: readonly SupplierCreditLineInput[]
  /** Where the goods leave from; needed with lines. */
  locationId?: string | null
  /** The credit's amount when no goods go back (a corrected price). */
  amountPaisa?: Paisa
  allocations?: readonly AllocationInput[]
}

/** What a line of returned goods is worth: quantity times cost, rounded like a receipt line. */
export function creditLineAmount(line: { qtyMilli: Milli; unitCostPaisa: Paisa }): Paisa {
  return mulDivRound(line.qtyMilli, line.unitCostPaisa, 1000)
}

export function buildSupplierCredit(
  ctx: BuildContext,
  input: BuildSupplierCreditInput,
): {
  credit: SupplierCreditRow
  lines: SupplierCreditLineRow[]
  movements: StockMovementRow[]
  allocations: SupplierAllocationRow[]
  ops: SyncOp[]
} {
  const goods = (input.lines ?? []).filter((l) => l.qtyMilli !== 0)
  for (const l of goods) {
    if (!Number.isSafeInteger(l.qtyMilli) || l.qtyMilli < 0)
      throw new CoreError('invalid_input', 'a returned quantity must be positive')
    if (!Number.isSafeInteger(l.unitCostPaisa) || l.unitCostPaisa < 0)
      throw new CoreError('invalid_input', 'a cost cannot be negative')
  }
  if (goods.length > 0 && !input.locationId)
    throw new CoreError('invalid_input', 'returned goods need the place they leave from')
  const total =
    goods.length > 0
      ? goods.reduce((sum, l) => sum + creditLineAmount(l), 0)
      : (input.amountPaisa ?? 0)
  positive(total, 'the credit')

  const credit: SupplierCreditRow = {
    ...base(ctx, input.id),
    supplier_id: input.supplierId,
    goods_receipt_id: input.goodsReceiptId ?? null,
    credit_no: text(input.creditNo),
    credit_date: input.creditDate,
    total_paisa: total,
    reason: text(input.reason),
    notes: text(input.notes),
    ...ACTIVE,
  }
  const lines: SupplierCreditLineRow[] = goods.map((l, i) => ({
    ...base(ctx),
    supplier_credit_id: credit.id,
    line_no: i + 1,
    product_id: l.productId,
    qty_milli: l.qtyMilli,
    unit_cost_paisa: l.unitCostPaisa,
    batch_id: l.batchId ?? null,
  }))
  const movements: StockMovementRow[] = lines.map((l) => ({
    ...base(ctx),
    product_id: l.product_id,
    location_id: input.locationId!,
    type: 'purchase_return',
    qty_milli: -l.qty_milli,
    unit_cost_paisa: l.unit_cost_paisa,
    ref_type: 'supplier_credit',
    ref_id: credit.id,
    notes: null,
    occurred_at: ctx.now,
    batch_id: l.batch_id,
  }))
  const allocations = allocationRows(
    ctx,
    { payment_id: null, credit_id: credit.id },
    total,
    input.allocations,
  )
  return {
    credit,
    lines,
    movements,
    allocations,
    ops: [
      op('supplier_credits', credit),
      ...lines.map((r) => op('supplier_credit_lines', r)),
      ...allocations.map((r) => op('supplier_allocations', r)),
      ...movements.map((r) => op('stock_movements', r)),
    ],
  }
}

// -----------------------------------------------------------------------------
// Cancelling
// -----------------------------------------------------------------------------

function voided<
  T extends {
    status: 'active' | 'void'
    voided_at: string | null
    void_reason: string | null
    updated_at: string
  },
>(ctx: BuildContext, row: T, reason: string): T {
  if (row.status !== 'active') throw new CoreError('immutable', 'it is already cancelled')
  if (!reason.trim()) throw new CoreError('invalid_input', 'cancelling needs a reason')
  return {
    ...row,
    status: 'void',
    voided_at: ctx.now,
    void_reason: reason.trim(),
    updated_at: ctx.now,
  }
}

/** Only a bill with nothing active set against it can be cancelled (the server checks too). */
export function voidSupplierBill(ctx: BuildContext, bill: SupplierBillRow, reason: string) {
  const next = voided(ctx, bill, reason)
  return { bill: next, ops: [op('supplier_bills', next)] }
}

/** Its allocations stop counting, so the bills it paid are owed again. */
export function voidSupplierPayment(
  ctx: BuildContext,
  payment: SupplierPaymentRow,
  reason: string,
) {
  const next = voided(ctx, payment, reason)
  return { payment: next, ops: [op('supplier_payments', next)] }
}

/**
 * Cancels a credit. The goods it sent back return to stock as an adjustment at the cost
 * they left at, in the same tx; pass the credit's own `purchase_return` movements.
 */
export function voidSupplierCredit(
  ctx: BuildContext,
  credit: SupplierCreditRow,
  reason: string,
  returned: readonly StockMovementRow[],
) {
  const next = voided(ctx, credit, reason)
  const movements: StockMovementRow[] = returned
    .filter((m) => m.type === 'purchase_return' && m.ref_id === credit.id)
    .map((m) => ({
      ...base(ctx),
      product_id: m.product_id,
      location_id: m.location_id,
      type: 'adjustment',
      qty_milli: -m.qty_milli,
      unit_cost_paisa: m.unit_cost_paisa,
      ref_type: 'supplier_credit',
      ref_id: credit.id,
      notes: 'Return to supplier cancelled',
      occurred_at: ctx.now,
      batch_id: m.batch_id,
    }))
  return {
    credit: next,
    movements,
    ops: [op('supplier_credits', next), ...movements.map((r) => op('stock_movements', r))],
  }
}

// -----------------------------------------------------------------------------
// What is owed
// -----------------------------------------------------------------------------

export type SupplierBillState = 'paid' | 'part' | 'unpaid'

/** A bill's state from its total and what active payments and credits cover. */
export function supplierBillState(totalPaisa: Paisa, coveredPaisa: Paisa): SupplierBillState {
  if (coveredPaisa >= totalPaisa) return 'paid'
  return coveredPaisa > 0 ? 'part' : 'unpaid'
}

/** Whole days a bill is past its due date (the bill date when it has none); 0 when not late. */
export function daysOverdue(dueDate: string, today: string): number {
  const ms = Date.parse(`${today}T00:00:00Z`) - Date.parse(`${dueDate}T00:00:00Z`)
  return ms > 0 ? Math.round(ms / 86_400_000) : 0
}
